/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import { Box, Tooltip, Typography } from "@mui/material";

import { useState } from "react";
import { Marker } from "react-map-gl/maplibre";
import { useAppSelector } from "../../../../store/hooks";
import { getSvgIconByTypeOrSubmode } from "../../../../utils/iconUtils";
import { useMapCurrentStop } from "../hooks/useMapCurrentStop";
import { useMarkerScale } from "../hooks/useMarkerScale";
import { NeighbourStopPopup } from "./NeighbourStopPopup";
import type { NeighbourStop } from "./types";

const NEIGHBOUR_SIZE = 36;

interface NeighbourMarkerItemProps {
  stop: NeighbourStop;
}

const NeighbourMarkerItem = ({ stop }: NeighbourMarkerItemProps) => {
  const [popupAnchor, setPopupAnchor] = useState<HTMLElement | null>(null);
  const scale = useMarkerScale();

  if (!stop.location) return null;

  const [lat, lng] = stop.location;
  const icon = getSvgIconByTypeOrSubmode(stop.submode, stop.stopPlaceType);

  return (
    <>
      <Marker latitude={lat} longitude={lng} anchor="center">
        <Tooltip title={stop.name || stop.id} placement="top" arrow>
          <Box
            onClick={(e) => setPopupAnchor(e.currentTarget)}
            sx={{
              position: "relative",
              width: Math.round(NEIGHBOUR_SIZE * scale),
              height: Math.round(NEIGHBOUR_SIZE * scale),
              borderRadius: "50%",
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "2px solid",
              borderColor:
                stop.permanentlyTerminated || stop.hasExpired
                  ? "error.main"
                  : "primary.main",
              boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              opacity: stop.permanentlyTerminated || stop.hasExpired ? 0.5 : 1,
              transition: "transform 0.15s",
              "&:hover": { transform: "scale(1.15)" },
            }}
          >
            {stop.isParent ? (
              <Typography
                sx={{
                  color: "text.primary",
                  fontWeight: 800,
                  fontSize: `${0.7 * scale}rem`,
                  lineHeight: 1,
                  letterSpacing: "0.05em",
                  userSelect: "none",
                }}
              >
                MM
              </Typography>
            ) : (
              <img
                src={icon}
                alt=""
                style={{
                  width: Math.round(20 * scale),
                  height: Math.round(20 * scale),
                }}
              />
            )}
            {stop.permanentlyTerminated && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  overflow: "hidden",
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: "141%",
                    height: "2px",
                    bgcolor: "error.main",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) rotate(45deg)",
                  }}
                />
              </Box>
            )}
          </Box>
        </Tooltip>
      </Marker>

      <NeighbourStopPopup
        anchorEl={popupAnchor}
        onClose={() => setPopupAnchor(null)}
        stop={stop}
        lat={lat}
        lng={lng}
      />
    </>
  );
};

export const NeighbourMarkers = () => {
  const neighbourStops = useAppSelector(
    (state) => (state.stopPlace as any).neighbourStops as NeighbourStop[],
  );
  // Group-aware (F-88): in the group view the previous stop is a neighbour.
  const current = useMapCurrentStop();
  const currentId = current?.id;
  const currentChildren = current?.isParent
    ? (current.children ?? []).map((child) => child.id)
    : [];
  const showExpiredStops = useAppSelector(
    (state) => (state.stopPlace as any).showExpiredStops as boolean,
  );
  const mergeSourceId = useAppSelector((state) =>
    (state as any).stopPlace?.mergeStopDialog?.isOpen
      ? ((state as any).stopPlace?.mergeStopDialog?.id as string | undefined)
      : undefined,
  );
  const isMergingStop = !!mergeSourceId;

  if (!neighbourStops?.length) return null;

  const visibleStops = neighbourStops.filter((stop) => {
    if (stop.id === currentId || currentChildren.includes(stop.id))
      return false;
    if (!showExpiredStops && (stop.hasExpired || stop.permanentlyTerminated))
      return false;
    if (isMergingStop && stop.id !== mergeSourceId) return false;
    return true;
  });

  return (
    <>
      {visibleStops.map((stop) => (
        <NeighbourMarkerItem key={stop.id} stop={stop} />
      ))}
    </>
  );
};
